import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEdit, faEraser, faFilter, faMagnifyingGlass, faSave, faShareNodes, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { PredefinedFilterSaveType } from 'src/app/components/dialogs/save-predefined-filter/models/dialog-save-predefined-filter-data';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { AttributeFilterBaseDirective } from 'src/app/core/directives/attribute-filter-base.directive';
import { FormFocusDirective } from 'src/app/core/directives/form-focus.directive';
import { FoldersListFilterHandler } from 'src/app/core/folders-list-filter-handler';
import { HttpError } from 'src/app/core/http-error';
import { AvmAttributeFilterSubstringPipe } from 'src/app/core/pipes/avm-attribute-filter-substring.pipe';
import { AvmColorPipe } from 'src/app/core/pipes/avm-color.pipe';
import { AvmDateAndTimePipe } from 'src/app/core/pipes/avm-date-and-time.pipe';
import { AvmDatePipe } from 'src/app/core/pipes/avm-date.pipe';
import { PropertyType } from 'src/app/models/enums/property-type';
import { FoldersListConfiguration } from 'src/app/models/folders-list-configuration';
import { IFilterable } from 'src/app/models/interfaces/filterable.interface';
import { PredefinedFilter } from 'src/app/models/predefined-filter';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { Colors } from 'src/app/models/server/enums/colors';
import { Rights } from 'src/app/models/server/enums/rights';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { PredefinedFiltersService } from 'src/app/services/http/predefined-filters.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { FolderSearchStateService } from 'src/app/services/state/folder-search-state.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-folders-list-search-bar',
    templateUrl: './folders-list-search-bar.component.html',
    styleUrls: ['./folders-list-search-bar.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        FaIconComponent,
        MatInputModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule,
        MatCheckboxModule,
        NgClass,
        MatChipsModule,
        MatIconModule,
        MatDatepickerModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
        MatListModule,
        AvmDatePipe,
        AvmDateAndTimePipe,
        AvmColorPipe,
        AvmAttributeFilterSubstringPipe,
        MatSelectModule,
    ],
})
export class FoldersListSearchBarComponent extends AttributeFilterBaseDirective implements OnInit, OnDestroy, AfterViewInit, IFilterable {
    /**
     * Référence des icones utilisées dans le template
     */
    public resetFilterIcon = faEraser;
    public filterIcon = faFilter;
    public searchIcon = faMagnifyingGlass;
    public warningIcon = faTriangleExclamation;
    public saveIcon = faSave;
    public editIcon = faEdit;
    public deleteIcon = faTrash;
    public shareIcon = faShareNodes;

    /**
     * Références des Enums utilisées dans le template.
     */
    public Colors = Colors;
    public Type = PropertyType;

    /**
     * Références des constantes utilisées dans le template.
     */
    public BOX_ID = FolderPropertyCodes.BOX_ID;

    /**
     * Valeur indiquant si l'utilisateur a le droit de gérer les filtres prédéfinis.
     */
    public managePredefinedFiltersRight: boolean = false;

    /**
     * Références des composants utilisés pour filtrer un attribut.
     */
    @ViewChildren('filterChip') filterChips: QueryList<ElementRef> = new QueryList<ElementRef>();

    /**
     * Configuration du composant.
     */
    private configuration$ = new BehaviorSubject<FoldersListConfiguration>(null);

    /**
     * Définit la configuration du composant.
     */
    @Input()
    set configuration(value) {
        this.configuration$.next(value);
    }

    /**
     * Obtient la configuration du composant.
     */
    get configuration() {
        return this.configuration$.getValue();
    }

    /**
     * Formulaire de recherche.
     */
    public searchForm: UntypedFormGroup;

    /**
     * Liste des attributs pouvant être filtrés (Ne contient que les attributs pour lequels aucun filtre n'est définit)*/
    public availableAttributesList: AttributeDefinition[] = [];

    /**
     * Liste des attributs pour lequels un filtre est définit
     */
    public filteredAttributesList: AttributeDefinition[] = [];

    /**
     * Gestionnaire de filtres associé au composant.
     */
    public currentFilterHandler: FoldersListFilterHandler;

    /**
     * Nombre de filtres appliqués.
     * (Utilisé pour déterminer l'ajout d'un nouveau filtre dans la liste des filtres)
     */
    private filtersCount: number = 0;

    /**
     * @deprecated
     * Valeur indiquant si un filtre automatique (enregistré localement sur le navigateur lors du precedent affichage de la vue) est présent.
     */
    public autoFiltersPresence: boolean = false;

    /**
     * Configuration interne du composant.
     */
    public _internalConfiguration: FoldersListConfiguration;

    /**
     * Filtre prédéfini actuellement appliqué à la liste des dossiers.
     */
    public currentPredefinedFilter: PredefinedFilter = new PredefinedFilter();

    /**
     * Indique si un filtre a été appliqué par l'utilisateur.
     * (Si la valeur est false cela indique qu'il s'agit d'un filtre prédéfini)
     */
    private isFilterAppliedByUser: boolean = false;

    /**
     * Id de l'utilisateur connecté.
     */
    public connectedUserId: string = null;

    /**
     * Constructeur.
     * @param formBuilder : Instance de FormBuilder utilisé pour créer le formulaire.
     * @param folderSearchStateService : Instance du service de gestion de l'état de la recherche des dossiers.
     * @param connectedUserStateService : Instance du service de gestion de l'état de l'utilisateur connecté.
     * @param dialogService : Instance du service de gestion des boites de dialogue.
     * @param predefinedFiltersService : Instance du service de gestion des filtres prédéfinis.
     * @param notificationService : Instance du service de gestion des notifications.
     * @param cdr : Classe utilisé pour detecter les changements.
     */
    constructor(
        private formBuilder: UntypedFormBuilder,
        private folderSearchStateService: FolderSearchStateService,
        private connectedUserStateService: ConnectedUsersStateService,
        private dialogService: DialogService,
        private predefinedFiltersService: PredefinedFiltersService,
        private notificationService: NotificationService,
        private cdr: ChangeDetectorRef
    ) {
        super();

        this.searchForm = this.formBuilder.group({
            searchControl: [''],
        });

        this.currentFilterHandler = new FoldersListFilterHandler(this);

        this.connectedUserId = this.connectedUserStateService.getUserConnected().userId;
        this.managePredefinedFiltersRight = this.connectedUserStateService.hasRight(Rights.ManagePredefinedFilters);
    }

    /**
     * Appelé à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.subscribeToConfigurationChangeEvent();
    }

    /**
     * Appelé après l'initialisation de la vue.
     */
    ngAfterViewInit(): void {
        this.subscribeToAppliedFiltersChange();
    }

    /**
     * Appelé à la destruction du composant.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this.currentFilterHandler) {
            this.currentFilterHandler.dispose();
        }
    }

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Rechercher'.
     */
    public onSearch() {
        this.applyFilters();
    }

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Réinitialiser'.
     */
    public onUndo() {
        this.reset();
    }

    /**
     * Appellée lorsque l'utilisateur ajoute un attribut à filtrer
     * @param attribute attribut à filtrer
     */
    public onAddAttributeToFilter(attribute: AttributeDefinition) {
        this.isFilterAppliedByUser = true;

        // Supprime de la liste des attributs l'attribut sélectionné
        this.availableAttributesList.splice(
            this.availableAttributesList.findIndex((a) => a.propertyCode === attribute.propertyCode),
            1
        );
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Ajoute l'attribut sélectionné dans la liste des attributs sélectionnés
        this.filteredAttributesList.push(attribute);
    }

    /**
     * Appelée lorsque l'utilisateur retire un attribut du filtre.
     * @param attributefiltered Attribut à retirer du filtre.
     */
    public onRemoveAttributeFromFilter(attributefiltered: AttributeDefinition) {
        this.deleteFilterCriteria(attributefiltered);
    }

    /**
     * Applique les filtres à la liste des dossiers.
     * (Appellé par le gestionnaire de filtres FoldersListFilterHandler)
     */
    public applyFilters() {
        this.currentPredefinedFilter.viewId = this._internalConfiguration.viewId;
        this.currentPredefinedFilter.searchTerm = this.getSearchTerm();
        this.currentPredefinedFilter.filteredAttributes = this.currentFilterHandler.filterValues;
        this.folderSearchStateService.setPredefinedFilterChange(this.currentPredefinedFilter);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un filtre prédéfini à appliquer.
     * @param predefinedFilter Filtre prédéfini.
     */
    public onApplyPredefinedFilter(predefinedFilter: PredefinedFilter) {
        this.currentPredefinedFilter = predefinedFilter.createCopy();
        this.setPredefinedFilter(predefinedFilter);
        this.applyFilters();
    }

    /**
     * Emis lorsque l'utilisateur clique sur le menu 'Enregistrer' d'un filtre prédéfini.
     */
    public onSavePredefinedFilter() {
        let data = this.currentPredefinedFilter.createCopy();
        data.name = null;

        this.dialogService.openSavePredefinedFilter(PredefinedFilterSaveType.SaveNew, data).subscribe((x) => {
            if (x) {
                this.currentPredefinedFilter = x;
                this.savePredefinedFilter(this.currentPredefinedFilter);
            }
        });
    }

    /**
     * Emis lorsque l'utilisateur clique sur le menu 'Enregistrer sous...' d'un filtre prédéfini.
     */
    public onSavePredefinedFilterUnder() {
        let data = this.currentPredefinedFilter.createCopy();
        data.id = null;
        data.name = null;

        this.dialogService.openSavePredefinedFilter(PredefinedFilterSaveType.SaveNew, data).subscribe((x) => {
            if (x) {
                this.currentPredefinedFilter = x;
                this.savePredefinedFilterUnder(this.currentPredefinedFilter);
            }
        });
    }

    /**
     * Emis lorsque l'utilisateur clique sur le menu 'Modifier...' d'un filtre prédéfini.
     */
    public onEditPredefinedFilter() {
        let data = this.currentPredefinedFilter.createCopy();

        this.dialogService.openSavePredefinedFilter(PredefinedFilterSaveType.SaveNew, data).subscribe((x) => {
            if (x) {
                this.currentPredefinedFilter = x;
                this.updatePredefinedFilter(this.currentPredefinedFilter);
            }
        });
    }

    /**
     * Emis lorsque l'utilisateur clique sur l'icône 'Supprimer' d'un filtre prédéfini.
     */
    public onDeletePredefinedFilter(predefinedFilter: PredefinedFilter) {
        let data = predefinedFilter.createCopy();

        this.dialogService.openDeletePredefinedFilter(data).subscribe((x) => {
            if (x === true) {
                this.deletePredefinedFilter(predefinedFilter.id);
            }
        });
    }

    /**
     * S'abonne aux evenements d'ajout ou de suppression de filtres.
     */
    private subscribeToAppliedFiltersChange() {
        this.filterChips.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            if (this.filterChips.length > 0 && this.filterChips.length > this.filtersCount) {
                // Un nouveau filtre a été ajouté.
                this.filtersCount++;
                // Simule un Click sur le filtre dernièrement créé pour qu'il s'affiche en mode édition sauf s'il s'agit d'un filtre prédéfini.
                if (this.isFilterAppliedByUser === true) {
                    this.filterChips.last.nativeElement.click();
                }
            }
        });
    }

    /**
     * S'abonne aux evenements de changement de configuration.
     */
    private subscribeToConfigurationChangeEvent() {
        this.configuration$.subscribe((x: FoldersListConfiguration) => {
            if (x) {
                this._internalConfiguration = x;
                if (x.attributeDefinitions && x.attributeDefinitions.length > 0) {
                    // Réinitialise tout.
                    this.availableAttributesList = [];
                    this.filteredAttributesList = [];
                    this.filtersCount = 0;
                    this.autoFiltersPresence = false;
                    this.isFilterAppliedByUser = false;

                    this.currentPredefinedFilter = x.defaultFilter;
                    this.BuildAvailableAttributesList(x.attributeDefinitions);
                    this.setPredefinedFilter(x.defaultFilter);
                    this.applyFilters();
                }
            }
        });
    }

    /**
     * Enregistre les modifications du filtre courant.
     * @param predefinedFilter : Filtre prédéfini.
     */
    private updatePredefinedFilter(predefinedFilter: PredefinedFilter) {
        this.predefinedFiltersService.update(predefinedFilter).subscribe((x) => {
            this.notificationService.displaySuccess(`Le filtre a été modifié.`, '');
            this.currentPredefinedFilter = x;
            let index = this._internalConfiguration.predefinedFilters.findIndex((x) => x.id === predefinedFilter.id);
            this._internalConfiguration.predefinedFilters.splice(index, 1);
            this._internalConfiguration.predefinedFilters.push(x);
            this._internalConfiguration.predefinedFilters = this._internalConfiguration.predefinedFilters.sort((x, y) => x.name.localeCompare(y.name));
        });
    }

    /**
     * Enregistre le filtre courant en tant que nouveau filtre.
     * @param predefinedFilter : Filtre prédéfini.
     */
    private savePredefinedFilter(predefinedFilter: PredefinedFilter) {
        this.predefinedFiltersService.add(predefinedFilter).subscribe(
            (x) => {
                this.notificationService.displaySuccess(`Le filtre a été sauvegardé.`, '');
                this.currentPredefinedFilter = x;
                this._internalConfiguration.predefinedFilters.push(x);
                this._internalConfiguration.predefinedFilters = this._internalConfiguration.predefinedFilters.sort((x, y) => x.name.localeCompare(y.name));
            },
            (error: HttpError) => {
                let errorMessage = error.message;
                if (error.code == 409) {
                    errorMessage = 'Un filtre avec un nom identique existe pour cette vue';
                }
                this.notificationService.displayError('Erreur', `Impossible de sauvegarder le filtre. ${errorMessage}`);
            }
        );
    }

    /**
     * Enregistre le filtre prédéfini courant sous un nouveau nom.
     * @param predefinedFilter : Filtre prédéfini.
     */
    private savePredefinedFilterUnder(predefinedFilter: PredefinedFilter) {
        this.predefinedFiltersService.add(predefinedFilter).subscribe((x) => {
            this.notificationService.displaySuccess(`Le filtre a été sauvegardé.`, '');
            this.currentPredefinedFilter = x;
            this._internalConfiguration.predefinedFilters.push(x);
            this._internalConfiguration.predefinedFilters = this._internalConfiguration.predefinedFilters.sort((x, y) => x.name.localeCompare(y.name));
        });
    }

    /**
     * Supprime le filtre prédéfini.
     * @param predefinedFilterId : Id du filtre prédéfini.
     */
    private deletePredefinedFilter(predefinedFilterId: number) {
        this.predefinedFiltersService.delete(predefinedFilterId).subscribe((x) => {
            this.notificationService.displaySuccess(`Le filtre a été supprimé.`, '');

            if (this.currentPredefinedFilter.id === predefinedFilterId) {
                this.reset();
            }

            let index = this._internalConfiguration.predefinedFilters.findIndex((x) => x.id === predefinedFilterId);
            this._internalConfiguration.predefinedFilters.splice(index, 1);
        });
    }

    /**
     * Construit la liste des attributs disponibles pour les filtres.
     * @param definitions : Liste des définitions d'attributs de la vue.
     */
    private BuildAvailableAttributesList(definitions: AttributeDefinition[]) {
        definitions = definitions.filter((a) => a.propertyCode !== FolderPropertyCodes.BOX_ID && a.propertyCode !== FolderPropertyCodes.VIEW_ID);
        definitions = definitions.sort((a, b) => a.header.localeCompare(b.header));
        this.availableAttributesList = definitions;
    }

    /**
     * Spécifit le filtre prédéfini à appliquer.
     * @param predefinedFilter Filtre prédéfini à appliquer.
     */
    private setPredefinedFilter(predefinedFilter: PredefinedFilter) {
        // Specifie le terme à rechercher
        this.setSearchTerm(predefinedFilter.searchTerm);

        // Supprime les attributs sélectionnés
        this.filteredAttributesList.forEach((x) => {
            // Ajoute l'attribut sélectionné dans la liste des attributs
            this.availableAttributesList.push(x);
        });

        // Reset le filtre
        if (this.currentFilterHandler) {
            this.currentFilterHandler.dispose();
            this.currentFilterHandler = new FoldersListFilterHandler(this);
        }

        this.filteredAttributesList = [];
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Met à jour le compteur de filtre
        this.filtersCount = 0;

        this.currentFilterHandler.initializeAttributeFilters(Object.assign([], this.availableAttributesList), predefinedFilter);

        this.cdr.detectChanges();

        predefinedFilter.filteredAttributes.getKeys().forEach((key) => {
            // Supprime de la liste des attributs l'attribut sélectionné
            const attributeIndex = this.availableAttributesList.findIndex((a) => a.propertyCode.toLowerCase() === key.toLowerCase());
            let attribute = this.availableAttributesList[attributeIndex];

            this.availableAttributesList.splice(attributeIndex, 1);
            this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

            // Ajoute l'attribut sélectionné dans la liste des attributs sélectionnés
            this.filteredAttributesList.push(attribute);
        });
    }

    /**
     * Supprime tous les critères de filtres appliqués à la liste des dossiers.
     */
    private deleteAllFilterCriterias(applyFilter: boolean = true) {
        // Supprime les attributs sélectionnés
        this.filteredAttributesList.forEach((x) => {
            // Ajoute l'attribut sélectionné dans la liste des attributs
            this.availableAttributesList.push(x);
        });

        // Reset le filtre
        if (this.currentFilterHandler) {
            this.currentFilterHandler.dispose();
            this.currentFilterHandler = new FoldersListFilterHandler(this);
            this.currentFilterHandler.initializeAttributeFilters(Object.assign([], this.availableAttributesList), null);

            if (applyFilter) {
                this.applyFilters();
            }
        }

        this.filteredAttributesList = [];
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Met à jour le compteur de filtre
        this.filtersCount = 0;
    }

    /**
     * Supprime un critère de filtre appliqué à la liste des dossiers.
     * @param attribute : Attribut à retirer du filtre.
     */
    private deleteFilterCriteria(attribute: AttributeDefinition) {
        // Supprime de la liste des attributs sélectionnés l'attribut sélectionné
        this.filteredAttributesList.splice(
            this.filteredAttributesList.findIndex((a) => a.propertyCode === attribute.propertyCode),
            1
        );

        // Ajoute l'attribut sélectionné dans la liste des attributs
        this.availableAttributesList.push(attribute);
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Reset le filtre
        if (this.currentFilterHandler) {
            this.currentFilterHandler.resetAttributeFilter(attribute.propertyCode);
        }

        this.currentPredefinedFilter.filteredAttributes.remove(attribute.propertyCode);

        // Met à jour le compteur de filtre
        this.filtersCount--;
    }

    /**
     * Réinitialise la recherche et les filtres.
     */
    private reset() {
        this.autoFiltersPresence = false;
        this.searchForm.reset();
        this.currentPredefinedFilter = new PredefinedFilter();
        this.currentPredefinedFilter.viewId = this._internalConfiguration.viewId;
        this.deleteAllFilterCriterias();
    }

    /**
     * Retourne la valeur du champ 'Terme à rechercher'.
     * @returns La valeur du champ 'Terme à rechercher'.
     */
    private getSearchTerm() {
        let value = this.searchForm.controls['searchControl'].value as string;

        if (!!value) {
            return value.trim();
        } else {
            return null;
        }
    }

    /**
     * Specifie la valeur du champ 'Terme à rechercher'.
     * @param value La valeur du champ 'Terme à rechercher'.
     */
    private setSearchTerm(value: string) {
        this.searchForm.controls['searchControl'].setValue(value);
    }
}
